import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { AppConstans } from '../../appConstants';
import { CreateResultDto } from '../../dto/create-result-dto';
import { DrevinaItemDto } from '../../dto/DrevinaItemDto';
import { FormInfoByCisloJrDto } from '../../dto/FormInfoByCisloJrDto';
import { InfoFromCisloUjDto } from '../../dto/InfoFromCisloUjDto';
import { ResultInfoGenericDto } from '../../dto/ResultInfoDto';
import { SelectItem } from '../../interfaces/select-item';
import { CiselnikyService } from '../../services/ciselniky.service';
import { Erma2Service } from '../../services/erma2.service';
import { FileDownloadService } from '../../services/file-download.service';
import { FillFormService } from '../../services/fill-form.service';
import { PdsMessageService } from '../../Services/pds-message.service';
import { ZadostPopService } from '../../services/zadost-pop.service';
import { CiselnikyUtils } from '../../Utils/ciselniky.utils';
import { FormTouchUtil } from '../../Utils/form-touch-util';
import { CjrMaLicenciAsyncValidator } from '../../validators/cjr.ma.licenci.async.validator';
import { DruhOstatnihoRmPopValidator } from '../../validators/druh.ostatniho.rm.pop.validator';
import { MnozstviOstValidator } from '../../validators/mnozstvi.ost.validator';
import { MnozstviSemValidator } from '../../validators/mnozstvi.sem.validator';
import { PopSberDoValidator } from '../../validators/pop.sber.do.validator';
import { PuvodOblastUjValidator } from '../../validators/puvod.oblast.uj.validator';
import { PuvodUjValidator } from '../../validators/puvod.uj.validator';
import { RokZraniRequiredValidator } from '../../validators/rok.zrani.required.validator';
import { UjAsyncValidator } from '../../validators/uj.async.validator';
import { VekAZpusobPestovaniRequiredValidator } from '../../validators/vek.a.zpusob.pestovani.required.validator';
import { FormBase } from '../form-base';


@Component({
    selector: 'app-form-zadost-pop',
    templateUrl: './form-zadost-pop.component.html',
    styleUrls: ['./form-zadost-pop.component.css'],
    standalone: false
})
export class FormZadostPopComponent extends FormBase implements OnInit {

  kategorieRMList: SelectItem[];
  druhRMList: SelectItem[];
  typZdrojeRMList: SelectItem[];
  pobockaList: SelectItem[];
  puvodList: SelectItem[];
  ucelPouzitiList: SelectItem[];
  druhRostlinList: SelectItem[];
  vyskovePasmoList: SelectItem[];
  provenienceList: SelectItem[];
  druhDrevinyList: DrevinaItemDto[];

  private _neautochtonniNeindigenniKod: string = 'NAN';
  private _semennyRm: number = 1;
  private _sadebniRm: number = 2;
  private _componentName: string = 'FormZadostPopComponent';
  private _initZadostId: number;
  private _initToken: string;

  override readonly MSG_KEY: string = 'zadost-o-pop-messages';

  /**
   * Příznak, že jde o materiál dovezený z ciziny (není česká UJ) - identifikuje se podle pobočky pověřené osoby
   */
  isDovoz: boolean = false;

  constructor(
    private fb: UntypedFormBuilder,
    private zadostPopService: ZadostPopService,
    private ciselnikyService: CiselnikyService,
    private fillFormService: FillFormService,
    private titleService: Title,
    private ujAsyncValidator: UjAsyncValidator,
    private rokZraniValidator: RokZraniRequiredValidator,
    private vekAZpPestovaniValidator: VekAZpusobPestovaniRequiredValidator,
    private licenceValidator: CjrMaLicenciAsyncValidator,
    private ciselnikyUtils: CiselnikyUtils,
    private druhOstatnichRmValidator: DruhOstatnihoRmPopValidator,
    private puvodOblastUjValidator: PuvodOblastUjValidator,
    private puvodUjValidator: PuvodUjValidator,
    private sberDoValidator: PopSberDoValidator,
    downloadService: FileDownloadService,
    formTouch: FormTouchUtil,
    protected pdsMessageService: PdsMessageService,
    erma2Service: Erma2Service,
    private route: ActivatedRoute
  ) {
    super(downloadService, formTouch, pdsMessageService, erma2Service);

    this.ujAsyncValidator.ujInfoEvent.subscribe((resp: InfoFromCisloUjDto) => {
      if (!this.validationBeforeSubmit) this.completeByEvCisloUj(resp);
    });

    this.licenceValidator.licenceEvent.subscribe((resp: FormInfoByCisloJrDto) => {
      if (!this.validationBeforeSubmit) this.completeByCisloJr(resp);
    });

    this.form = this.fb.group({
      cislo: [''],
      rok: [new Date().getFullYear(), Validators.required],
      pobockaPovereneOsoby: [null, Validators.required],
      dodavatel: this.fb.group({
        jmeno: [''],
        prijmeni: [''],
        datNaroz: [''],
        obchNaz: [''],
        adresaTrvalehoPobytuFo: [''],
        adresaPodnikaniFo: [''],
        adresaSidlaPo: [''],
        ico: [''],
      }),
      licence: this.fb.group({
        cj: [''],
        zeDne: [''],
        nabylaPravniMoci: [''],
        cisloJr: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.licenceValidator] }]
      }),
      vztazenoKOznameni: this.fb.group({
        cislo: ['', Validators.required],
        zeDne: ['', Validators.required]
      }),
      mnozstviMaterialu: ['', Validators.required],
      mnozstviMaterialuSem: ['', { updateOn: 'blur', validators: [MnozstviSemValidator(this._componentName, this._semennyRm)] }],
      mnozstviMaterialuOst: ['', { updateOn: 'blur', validators: [MnozstviOstValidator(this._componentName, this._semennyRm)] }],
      druhRostlin: ['', { updateOn: 'blur', validators: [this.druhOstatnichRmValidator] }],
      datumSberu: this.fb.group({
        od: ['', Validators.required],
        do: ['', { updateOn: 'blur', validators: [Validators.required, this.sberDoValidator] }]
      }),
      oblastProvenience: [''],
      druhDreviny: this.fb.group({
        nazevCesky: ['', Validators.required],
        nazevVedecky: ['', Validators.required],
        oznaceniKlonu: ['']
      }),
      repMaterialKategorie: ['', Validators.required],
      druhRepMaterialu: [null, Validators.required],
      typZdrojeRepMaterialu: ['', Validators.required],
      puvod: ['', { updateOn: 'blur', validators: [this.puvodUjValidator] }],
      puvodRm: ['', { updateOn: 'blur', validators: [this.puvodOblastUjValidator] }],
      vyskovePasmo: ['', Validators.required],
      evCisloUj: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.ujAsyncValidator] }],
      genModifikovanyMaterial: [false],
      ucelPouziti: [null, Validators.required],
      rokZrani: ['', { updateOn: 'blur', validators: [this.rokZraniValidator] }],
      zpusobPestovani: ['', { updateOn: 'blur', validators: [this.vekAZpPestovaniValidator] }],
      misto: ['', Validators.required],
      datum: [new Date().toISOString().substring(0, 10)]
    });
  } // constructor

  callService(): Observable<ResultInfoGenericDto<CreateResultDto>> {
    return this.zadostPopService.createNew(this.form.value);
  }


  ngOnInit(): void {
    this.titleService.setTitle("ERMA2 - Žádost o potvrzení o původu");

    this.route.params.subscribe(params => {
      this._initZadostId = params.zadostId;
      this._initToken = params.token;
      this.setCiselnikyAndData();
    });

    this.druhRepMaterialu?.valueChanges.subscribe(_ => {
      if (!this.validationBeforeSubmit) this._druhRmChangeHandler();
    });

    this.pobockaPovereneOsoby?.valueChanges.subscribe(changedValue => {
      if (!this.validationBeforeSubmit) this._pobockaChangeHandler(changedValue);
    });

    this.form.get('puvod').valueChanges.subscribe(_ => {
      if (!this.validationBeforeSubmit) this.revalidateControl(this.form.get('puvodRm'), false);
    });

    //uj.async.validator se nezavolá, pokud vymažu hodnotu políčka, proto tento changeHandler.
    this.form.get('evCisloUj')?.valueChanges.subscribe(changedValue => {
      if (changedValue == void 0 || changedValue == '') {
        this._clearDataDleUj();
      }
    });
  }


  get druhRepMaterialu(): AbstractControl {
    return this.form.get('druhRepMaterialu');
  }

  get pobockaPovereneOsoby(): AbstractControl {
    return this.form.get('pobockaPovereneOsoby');
  }

  get evCisloUj(): AbstractControl {
    return this.form.get('evCisloUj');
  }

  get isSemRm(): boolean {
    return this.druhRepMaterialu?.value?.kod == this._semennyRm;
  }

  get isSadRm(): boolean {
    return this.druhRepMaterialu?.value?.kod == this._sadebniRm;
  }

  get druhRmHasValue(): boolean {
    return this.druhRepMaterialu?.value != void 0 && this.druhRepMaterialu?.value != '';
  }

  get sberDo() {
    return this.form.get('datumSberu')?.get('do');
  }

  /**
  * Načte číselníky ze serveru a naplní formulář
  * */
  setCiselnikyAndData() {
    this.ciselnikyService.getAll().subscribe(lists => {
      this.kategorieRMList = lists.kategorieRm;
      this.druhRMList = lists.druhRm;
      this.typZdrojeRMList = lists.typZdroje;
      this.pobockaList = lists.pobocka;
      this.provenienceList = lists.oblastProvenience;
      this.puvodList = lists.puvod;
      this.ucelPouzitiList = lists.ucelPouziti;
      this.druhRostlinList = lists.druhRostlin;
      this.vyskovePasmoList = lists.vyskovePasmo;
      this.druhDrevinyList = lists.druhDreviny;
      this._loadInitData();
    });
  }

  /**
   * Pokud je zname id zadosti a token, tak nacist data. Pokud neni zname, tak vyplnit cjr podle uzivatelke
   * */
  private _loadInitData(): void {
    if (this._initZadostId != void 0 && this._initToken != void 0) {
      this.zadostPopService.get(this._initZadostId, this._initToken).subscribe(res => {
        if (res.success) {
          res.data.vztazenoKOznameni.zeDne = res.data.vztazenoKOznameni.zeDne.substring(0, 10);
          res.data.datumSberu.od = res.data.datumSberu.od.substring(0, 10);
          res.data.datumSberu.od = res.data.datumSberu.od.substring(0, 10);
          res.data.datum = res.data.datum.substring(0, 10);
          res.data.mnozstviMaterialuSem = res.data.mnozstviMaterialuOst = res.data.mnozstviMaterialu;
          this.form.patchValue(res.data);
          this._pobockaChangeHandler(this.pobockaPovereneOsoby?.value);
        } else {
          this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: res.messages[0] })
        }
      });
    } else {
      this.fillFormService.getPrihlasenyUzivatelInfo().subscribe((resp: ResultInfoGenericDto<FormInfoByCisloJrDto>) => {
        if (resp != void 0 && resp.success) {
          this._setDodavatelData(resp.data);
          this._setCjr(resp?.data?.cisloJr);
        }
      });
    }
  }


  /**
   * Dle čísla jednotného registru doplní část údajů o licenci a dodavateli.
  **/
  completeByCisloJr(data: FormInfoByCisloJrDto) {
    this._resetDodavatelData();
    if (data != void 0) {
      this._setDodavatelData(data);
    }
  }


  /**
   * Naplní data dodavatele a licence.
   * @param data
   */
  private _setDodavatelData(data: FormInfoByCisloJrDto) {
    this.form.get('dodavatel')?.patchValue(data);
    this.form.get('licence')?.get('cj').setValue(data?.licence?.cj);
    this.form.get('licence')?.get('nabylaPravniMoci').setValue(data?.licence?.nabylaPravniMoci);
    this.form.get('licence')?.get('zeDne').setValue(data?.licence?.zeDne);
  }


  /**
   * Naplní/vyprázdní číslo jednotného registru.
   * @param cjr
   */
  private _setCjr(cjr: string): void {
    this.form.get('licence')?.get('cisloJr').setValue(cjr);
  }


  /**
   * Reset dat dodavatele a licence.
  **/
  private _resetDodavatelData(): void {
    this.form.get('dodavatel')?.reset();
    this.form.get('licence')?.patchValue({//cisloJr musi byt zachovano
      cj: '',
      zeDne: '',
      nabylaPravniMoci: ''
    });
  }

  /**
   * Po vyplnění čísla UJ doplní relevantní pole.
  **/
  private completeByEvCisloUj(ujInfo: InfoFromCisloUjDto) {
    if (!this.isDovoz) {
      if (ujInfo != void 0) {
        let druhDreviny = ujInfo.isValidCzCisloUj ? this.druhDrevinyList.find(x => x.zkratka == ujInfo.druhDreviny) : { nazevCesky: '', nazevVedecky: '' };
        let puvodRm = '';
        if (ujInfo.puvodKod == this._neautochtonniNeindigenniKod) {
          let pPlo = this.provenienceList.find(x => x.value == ujInfo.puvodPlo);
          if (pPlo != void 0) puvodRm = pPlo?.value + ' - ' + pPlo?.label;
        }

        this.form.patchValue({
          oblastProvenience: this.ciselnikyUtils.SelectItemToCiselik(this.provenienceList.find(x => x.value == ujInfo.oblastProvenience)),
          repMaterialKategorie: this.ciselnikyUtils.SelectItemToCiselik(this.kategorieRMList.find(x => x.value == ujInfo.repMaterialKategorie)),
          typZdrojeRepMaterialu: this.ciselnikyUtils.SelectItemToCiselik(this.typZdrojeRMList.find(x => x.value == ujInfo.typZdrojeRepMaterialu)),
          vyskovePasmo: this.ciselnikyUtils.SelectItemToCiselik(this.vyskovePasmoList.find(x => x.value == ujInfo.vyskovePasmo)),
          druhDreviny: {
            nazevCesky: druhDreviny.nazevCesky,
            nazevVedecky: druhDreviny.nazevVedecky,
          },
          puvod: this.ciselnikyUtils.SelectItemToCiselik(this.puvodList.find(x => x.value == ujInfo.puvodKod)),
          puvodRm: puvodRm
        });
      }
      else {
        this._clearDataDleUj();
      }
    }
  }


  /**
   * Vymaže data načítané z UJ.
  **/
  private _clearDataDleUj(): void {
    this.form.patchValue({
      oblastProvenience: '',
      repMaterialKategorie: '',
      typZdrojeRepMaterialu: '',
      vyskovePasmo: '',
      druhDreviny: {
        nazevCesky: '',
        nazevVedecky: '',
      },
      puvod: '',
      puvodRm: ''
    });
  }

  compareSelect(s1: SelectItem, s2: SelectItem): boolean {
    return s1 && s2 ? s1.value === s2.value : s1 === s2;
  }

  /**
   * Dle pobočky pověřené osoby rozhodne, zda jde o RM z dovozu.
  **/
  setIsDovoz(pobockaKod: string) {
    //pokud jde o přechod nastavení pobočky z Mze na jinou, tak promazat závislé data na UJ
    if (this.isDovoz && pobockaKod != AppConstans.popVydalKodMze) this._clearDataDleUj();

    this.isDovoz = (pobockaKod == AppConstans.popVydalKodMze);
  }


  /**
   * Výkonná fce changehandleru pobočky pověřené osoby.
   * @param changedValue
   */
  private _pobockaChangeHandler(changedValue): void {
    let kod = changedValue != void 0 ? changedValue.kod : '';
    this.setIsDovoz(kod);
    this.evCisloUj?.clearAsyncValidators();
    this.evCisloUj?.clearValidators();
    if (this.isDovoz) {
      this.evCisloUj?.setAsyncValidators(null);
      this.evCisloUj?.setValidators([Validators.required, Validators.pattern(/^(?!.*(CZ|cz)).*$/)]);
    } else {
      this.evCisloUj?.setAsyncValidators(this.ujAsyncValidator.validateFn(this.evCisloUj));
      this.evCisloUj?.setValidators([Validators.required]);
    }

    super.revalidateControl(this.evCisloUj, true);
  };


  /**
   * Výkonná fce changehandleru druhu RM.
  **/
  private _druhRmChangeHandler(): void {
    if (this.isSemRm) {
      this.form.get('zpusobPestovani')?.reset();
    }
    else if (this.isSadRm) {
      this.form.get('rokZrani')?.reset();
    }
    else {
      this.form.get('rokZrani')?.reset();
      this.form.get('zpusobPestovani')?.reset();
    }

    this.revalidateControl(this.form.get('druhRostlin'), true);
    this.revalidateControl(this.form.get('mnozstviMaterialuSem'), true);
    this.revalidateControl(this.form.get('mnozstviMaterialuOst'), true);
  }


  onSubmit(isIsds: boolean = false) {
    this._updateMnozstvi();

    this._showErrorMessages();
    super.onSubmit(isIsds);
  }


  /**
   * Aktualizace/překlopení množství do správné položky dle druhu RM.
  **/
  private _updateMnozstvi(): void {
    let druhRm = this.druhRepMaterialu?.value;

    if (druhRm != void 0 && druhRm != '') {
      let mnozstvi;
      if (druhRm.kod == this._semennyRm) {
        mnozstvi = this.form.get('mnozstviMaterialuSem')?.value;
      }
      else {
        mnozstvi = this.form.get('mnozstviMaterialuOst')?.value;
      }

      if (mnozstvi != void 0) {
        this.form.get('mnozstviMaterialu')?.setValue(mnozstvi);
      }
    }
  }

  /**
   * Zobrazení chyb po validaci formuláře.
  **/
  private _showErrorMessages(): void {
    this.pdsMessageService.clear(this.MSG_KEY);

    this.messageServiceFormError(this.MSG_KEY);

    // datum sběru do
    if (!this.sberDo?.valid && this.sberDo?.errors['sberDo'] != void 0) {
      this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: this.sberDo?.errors['sberDo'] });
    }
  }

}
